import { useEffect, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { useAnnoncesLegalesService } from '@europrocurement/l2d-domain';
import { castIriToId } from '@europrocurement/l2d-utils';
import { enqueueSnackbar } from 'notistack';
import { PrestationAnnonceLgaleApiGetListTblPrestaPrestationAnnonceLgaleCollectionRequest } from '@europrocurement/l2d-domain/openApi/ApiAnnoncesLegales';
import { LegalAnnoucementSuggestionProps } from '../components/detail/LegalAnnoucementSuggestion/LegalAnnoucementSuggestion';

export type Suggestion = Omit<LegalAnnoucementSuggestionProps, 'onClick'>;

const useSearchLegalAnnoucement = (sTypeId?: number) => {
    const [searching, setSearching] = useState<boolean>(false);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [filters, setFilters] =
        useState<PrestationAnnonceLgaleApiGetListTblPrestaPrestationAnnonceLgaleCollectionRequest>();
    const [suggestions, setSuggestions] = useState<Array<Suggestion>>([]);

    const { legalsAdvertApi, legalPrestationsApi } = useAnnoncesLegalesService();

    const search = useDebouncedCallback(async () => {
        setSearching(true);

        try {
            const prestations =
                await legalPrestationsApi.getListTblPrestaPrestationAnnonceLgaleCollection({
                    q: searchTerm,
                    dossierStatut: 4,
                    formaliste: true,
                    validationProcess: false,
                    typePrestaId: sTypeId,
                    ...filters,
                });

            const typeIds = prestations.data['hydra:member']
                .filter((prestation) => prestation.typePresta)
                .map((prestation) => castIriToId(prestation.typePresta as string))
                .filter((id, index, array) => array.indexOf(id) === index);
            const prestationTypes = typeIds
                ? await legalsAdvertApi.getListAnnoncesLegalesAnnonceLegaleCollection({
                      id2: typeIds,
                  })
                : { data: { 'hydra:member': [] } };

            const buffer = prestations.data['hydra:member'].map((row) => ({
                id: row.id as number,
                label: row.numero as string,
                category: prestationTypes.data['hydra:member'].find(
                    (type: { id: number }) => type.id === castIriToId(row.typePresta as string),
                )?.libelle as string,
                selected: false,
                newspaper: row.jal?.nom as string,
                advertiser: row.annonceur as string,
                dateDemande: row.dateDemande as string,
            }));

            setSuggestions(buffer);
        } catch (err) {
            console.error(err);
            enqueueSnackbar('Une erreur est survenue durant votre recherche', {
                variant: 'error',
            });
        } finally {
            setSearching(false);
        }
    }, 300);

    const selectSuggestion = (id: number) => {
        const tmp = suggestions.map((suggestion) => {
            let isSelected = false;

            if (suggestion.id === id) {
                isSelected = !suggestion.selected;
            }

            return {
                ...suggestion,
                selected: isSelected,
            };
        });

        setSuggestions(tmp);
    };

    useEffect(() => {
        if (searchTerm) {
            search();
        } else {
            setSuggestions([]);
        }
    }, [searchTerm, search, setSuggestions]);

    return {
        suggestions,
        searching,
        selectSuggestion,
        setSearchTerm,
        setFilters,
    };
};

export default useSearchLegalAnnoucement;
